import { useCallback, useEffect, useRef, useState } from "react";
import {
  getPolicyHistoryList,
  PolicyHistoryDto,
} from "utils/api/policyHistoryApi";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";

// PolicyHistory lacks the PolicyTransaction-specific fields the shared
// PolicyManagementRecord type requires (StageTypeCode, IsOnHold, etc.); fill
// them with safe defaults so the type stays satisfied without inventing data.
const dtoToRecord = (dto: PolicyHistoryDto): PolicyManagementRecord => ({
  ...dto,
  // PolicyTransactionID is `long?` on PolicyHistory but `long` on
  // PolicyTransaction — coerce null to 0 since the History row keys off
  // PolicyHistoryID anyway (via uniqueIdentifier below).
  PolicyTransactionID: dto.PolicyTransactionID ?? 0,
  BatchSeqNumber: null,
  ExternalFee: null,
  ExternalPremiumTax: null,
  ExternalRiskRate: null,
  ExternalRemittance: null,
  StageTypeCode: null,
  StageChangedDate: null,
  StateChangedByUserID: null,
  IdentityKey: 0,
  IsOnHold: false,
  OnHoldReason: null,
  LastHeldByUserID: null,
  LastHeldByUserName: null,
  LockedByUserID: null,
  LastOnHoldDate: null,
  HoldRemovedComment: null,
  HoldRemovedBy: null,
  HoldRemovedDate: null,
  IsForcePromoted: false,
  OpenErrorCount: 0,
  NoteCount: 0,
  LockedByUserName: null,
  LockContextActive: null,
  OverrideByUsername: null,
  OverrideDate: null,
  LockedDate: null,
  uniqueIdentifier: String(dto.PolicyHistoryID),
  isNational: !!dto.IsMAST,
  errors: [],
  policyNotes: [],
  policyActivity: [],
  policyAuditLog: [],
  parties: [],
  returnNote: null,
});

// Loader merges arbitrary criteria (header inputs + chip / advanced fragments)
// into one bag, so accept `unknown` here and narrow per type. Numbers/booleans
// (e.g. IsHOI 1/0, IsLocked, IsEdited) are passed through as-is rather than stringified,
// since the backend binds those criteria fields to bool/int, not string.
function serializeFilters(
  filters: Record<string, unknown>
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v == null) continue;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      out[k] = v.join(",");
    } else if (typeof v === "number" || typeof v === "boolean") {
      out[k] = v;
    } else if (typeof v === "string" && v.trim() !== "") {
      out[k] = v;
    }
  }
  return out;
}

export function usePolicyHistoryList(
  listCount: number,
  requestedPage: number,
  sortColumn: string,
  filters: Record<string, unknown> = {},
  enabled: boolean = true
) {
  const [{ profileSettings }] = useProfileSettingsCache();
  const userTimeZone = profileSettings?.userTimeZone ?? "";
  const [records, setRecords] = useState<PolicyManagementRecord[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reloadToken, setReloadToken] = useState(0);
  const reqIdRef = useRef(0);

  const refetch = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => {
    if (!enabled) return;
    if (!userTimeZone) return;
    const myReq = ++reqIdRef.current;
    setIsLoading(true);
    getPolicyHistoryList({
      UserTimeZone: userTimeZone,
      RequestedPage: requestedPage,
      ListCount: listCount,
      SortColumn: sortColumn,
      ...serializeFilters(filters),
    })
      .then((dtos) => {
        if (reqIdRef.current !== myReq) return;
        const first = dtos[0];
        setTotalRows(first?.TotalRows ?? dtos.length);
        setTotalPages(first?.TotalPages ?? (dtos.length > 0 ? 1 : 0));
        setRecords(dtos.map(dtoToRecord));
        setIsLoading(false);
      })
      .catch(() => {
        if (reqIdRef.current === myReq) setIsLoading(false);
      });
  }, [listCount, requestedPage, sortColumn, userTimeZone, filters, enabled, reloadToken]);

  return { records, totalRows, totalPages, isLoading, setRecords, refetch };
}

export default usePolicyHistoryList;